import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import * as R from 'ramda';

import SocialAccountsModal from '../components/social-accounts-modal/SocialAccountsModal';
import connect from '../utils/connect';

import sendNotification from '../actions/notifications/send';
import extendToken from '../actions/settings/extendToken';
import getFacebookLinkedAccounts from '../actions/settings/getFacebookLinkedAccounts';
import getInstagramLinkedAccounts from '../actions/settings/getInstagramLinkedAccounts';
import refreshFacebookPages from '../actions/settings/refreshFacebookPages';
import refreshInstagramAccounts from '../actions/settings/refreshInstagramAccounts';
import saveFbPages from '../actions/settings/saveFbPages';
import saveIgAccounts from '../actions/settings/saveIgAccounts';
import removeIgAccount from '../actions/settings/removeIgAccount';
import removeFbPage from '../actions/settings/removeFbPage';
import getClientRsps from '../actions/settings/getClientRsps';
import combineActions from '../utils/combineActions';
import prepareRenderList from '../utils/prepareRenderList';
import { useTranslation } from 'react-i18next';

import { Button, LoadingDots } from '@app/ui';

import logEvent from '../utils/logAuthEvents';

import '../styles/custom-myles-styles.scss';
import SocialMediaLinkageCard from './SocialMediaLinkageCard';
import { fbSdkURL } from '../utils/apiUrls';

const scopes =
    'pages_show_list,read_insights,pages_read_engagement,pages_read_user_content,ads_management,business_management';

const pullFacebookPages = response => {
    const {
        accounts: { data: fbPages },
        id: userId,
    } = response;
    return fbPages.reduce((acc, currVal) => {
        const { access_token: accessToken, id, name, link } = currVal;
        const picture = R.path(['picture', 'data', 'url'], currVal) || null;
        let username = R.path(['username'], currVal) || null;
        if (!username) {
            username = R.path(['page_token'], currVal) || null;
        }

        const pageData = {
            platformUserId: id,
            userId,
            platformId: 3,
            username,
            name,
            profilePictureUrl: picture,
            type: 'facebook_page',
            id: null,
            profileUrl: link,
            addedAt: null,
            token: {
                accessToken,
                tokenType: 'bearer',
                expire: null,
                revoked: false,
            },
        };
        acc[id] = pageData;
        return acc;
    }, {});
};

const FacebookManager = ({
    client,
    getFacebookLinkedAccounts,
    socialAccounts,
    triggerModalChange,
    login,
    modal,
    onCloseModal,
    refreshFacebookPages,
    saveFbPages,
    sendNotification,
    removeFbPage,
}) => {
    const { t } = useTranslation('settings');
    const [isSubmittingAccount, setIsSubmittingAccount] = useState(false);

    useEffect(() => {
        getFacebookLinkedAccounts(client.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    const _onLoginSuccess = useCallback(
        async token => {
            // Use the token passed from the parent component instead of relying on Redux state
            const accessToken = token;

            const batch = encodeURI(
                JSON.stringify([
                    {
                        method: 'GET',
                        relative_url:
                            'me?fields=id,name,accounts.limit(100){access_token,id,name,picture,page_token,username,link}',
                    },
                ]),
            );

            const response = await fetch(
                `${fbSdkURL}/me?limit=100&access_token=${accessToken}&batch=${batch}`,
                {
                    method: 'POST',
                },
            );
            const responseArray = await response.json();

            if (responseArray && responseArray.length > 0) {
                responseArray.forEach(({ body }) => {
                    const data = JSON.parse(body);
                    //eslint-disable-next-line no-prototype-builtins
                    if (data && data.hasOwnProperty('accounts')) {
                        const fbPages = pullFacebookPages(data);
                        refreshFacebookPages(fbPages);

                        logEvent({
                            type: 'link-list-all-available-accounts',
                            payload: {
                                platformId: 3,
                                platformName: 'facebook',
                                pages: fbPages,
                            },
                        });
                    }
                });

                triggerModalChange('pick-facebook-page');
            }
        },
        [refreshFacebookPages, triggerModalChange],
    );

    const _onLoginFailure = useCallback(() => {
        alert(t('clientConfiguration.social.notifications.genericError'));

        logEvent({
            type: 'link-request-to-3rd-party-failed',
            payload: {
                platformId: 3,
                platformName: 'facebook',
            },
        });
    }, [t]);

    const _onUnlink = useCallback(
        async page => {
            const { id: accountId, platformUserId } = page;
            await removeFbPage(client.id, accountId, platformUserId);

            logEvent({
                type: 'unlink',
                payload: {
                    accountId,
                    platformUserId,
                    platformId: 3,
                    platformName: 'facebook',
                },
            });
        },
        [client.id, removeFbPage],
    );

    const _onSubmit = useCallback(
        async selectedPages => {
            setIsSubmittingAccount(true);
            try {
                const { pages, accessToken: userAccessToken } =
                    socialAccounts.facebook;

                const clientAuthorizedAccounts = selectedPages.reduce(
                    (acc, platformUserId) => {
                        const page = pages[platformUserId];

                        const clientAuthorizedAccount = {
                            clientId: client.id,
                            account: page,
                            accessToken: page.token.accessToken,
                            platformUserId: page.userId,
                            userAccessToken,
                        };

                        acc.push(clientAuthorizedAccount);
                        return acc;
                    },
                    [],
                );

                await saveFbPages(client.id, clientAuthorizedAccounts);

                logEvent({
                    type: 'link-selected-accounts',
                    payload: {
                        pages: selectedPages,
                        platformId: 3,
                        platformName: 'facebook',
                    },
                });

                triggerModalChange(null);
                sendNotification(
                    t(
                        'clientConfiguration.social.notifications.demographicPopulate',
                    ),
                    {
                        description: t(
                            'clientConfiguration.social.notifications.impressionPopulate',
                        ),
                    },
                );
            } catch (error) {
                if (!error?.wasHandledByApi) {
                    sendNotification(
                        t(
                            'clientConfiguration.social.notifications.genericError',
                        ),
                        { context: 'danger' },
                    );
                }
            } finally {
                setIsSubmittingAccount(false);
            }
        },
        [
            client.id,
            socialAccounts.facebook,
            saveFbPages,
            triggerModalChange,
            sendNotification,
            t,
        ],
    );

    const { pages, linkedPages, isRequestingLinkedPages } =
        socialAccounts.facebook;

    const onLogin = useCallback(() => {
        login(scopes, _onLoginSuccess, _onLoginFailure, 'facebook');

        logEvent({
            type: 'link-request-to-3rd-party',
            payload: {
                platformId: 3,
                platformName: 'facebook',
            },
        });
    }, [login, _onLoginSuccess, _onLoginFailure]);

    const linkedPagesKeys = Object.keys(linkedPages);
    const facebookAccounts = Object.values(linkedPages).map(account => {
        if (account.name) return account;
        account.name = account.username;
        return account;
    });
    const renderList = prepareRenderList(facebookAccounts);

    return (
        <div className="_flex-card">
            <div className="gds-card -m-b-0">
                {linkedPagesKeys.length > 5 && <span className="-shadow" />}

                <div className="gds-card__block gds-card__block--divide-bottom -p-h-3">
                    <p className="gds-text--regular">
                        <i className="fa fa-facebook-square -m-r-2" />
                        {t('clientConfiguration.social.facebook.title')}
                    </p>
                </div>
                <div className="gds-card__block -p-h-3 -p-t-3 -p-b-1 -overflow-y-scroll -social-account-container">
                    <div
                        className="gds-flex gds-flex--justify-center"
                        style={{ height: '100%' }}
                    >
                        {isRequestingLinkedPages && <LoadingDots />}

                        {!isRequestingLinkedPages &&
                            linkedPagesKeys.length > 0 && (
                                <div
                                    className="gds-flex gds-flex--justify-center -m-b-2"
                                    style={{ width: '100%' }}
                                >
                                    <Button
                                        onClick={onLogin}
                                        size="small"
                                        variant="primary"
                                        style={{ width: '100%' }}
                                    >
                                        {t(
                                            'clientConfiguration.social.facebook.linkAnother',
                                        )}
                                    </Button>
                                </div>
                            )}

                        {!isRequestingLinkedPages &&
                            renderList.map(clientAuthorizedAccount => {
                                const account = clientAuthorizedAccount.account;

                                const revoked = clientAuthorizedAccount.revoked;

                                const id = account.id;
                                const name = account.name;
                                const platformUserId = account.platformUserId;

                                const accountLogo = `https://graph.facebook.com/${platformUserId}/picture`;

                                const _unlinkInternal = () =>
                                    _onUnlink(account);

                                return (
                                    <SocialMediaLinkageCard
                                        key={id}
                                        type="facebook"
                                        logo={accountLogo}
                                        revoked={revoked}
                                        _link={onLogin}
                                        _unlink={_unlinkInternal}
                                        name={name}
                                    />
                                );
                            })}

                        {!isRequestingLinkedPages &&
                            linkedPagesKeys.length === 0 && (
                                <Button
                                    onClick={onLogin}
                                    size="small"
                                    variant="primary"
                                    style={{ width: '100%' }}
                                >
                                    {t(
                                        'clientConfiguration.social.facebook.link',
                                    )}
                                </Button>
                            )}
                    </div>
                </div>
            </div>
            {modal === 'pick-facebook-page' && (
                <SocialAccountsModal
                    onCloseModal={onCloseModal}
                    items={pages}
                    linkedItems={linkedPagesKeys}
                    modal={modal}
                    onSubmit={_onSubmit}
                    isSubmitting={isSubmittingAccount}
                />
            )}
        </div>
    );
};

FacebookManager.propTypes = {
    facebook: PropTypes.object,
    client: PropTypes.object,
    triggerModalChange: PropTypes.func,
    login: PropTypes.func.isRequired,
    modal: PropTypes.string,
    onCloseModal: PropTypes.func.isRequired,
    getFacebookLinkedAccounts: PropTypes.func.isRequired,
    socialAccounts: PropTypes.object.isRequired,
    saveFbPages: PropTypes.func.isRequired,
    refreshFacebookPages: PropTypes.func.isRequired,
    removeFbPage: PropTypes.func.isRequired,
    sendNotification: PropTypes.func.isRequired,
};

FacebookManager.defaultProps = {
    facebook: {},
    user: {},
};

export default connect(
    ['socialAccounts', 'client'],
    combineActions({
        sendNotification,
        extendToken,
        getFacebookLinkedAccounts,
        getInstagramLinkedAccounts,
        refreshFacebookPages,
        refreshInstagramAccounts,
        saveFbPages,
        saveIgAccounts,
        removeIgAccount,
        removeFbPage,
        getClientRsps,
    }),
)(FacebookManager);
